import { PropsWithChildren } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Order, isOrderExecuted } from '#/unified-rfqs/entities/drfq/orders';
import { Drfq, isDrfq } from '#/unified-rfqs/entities/drfq/domain';
import { isOpenOrder } from '#/unified-rfqs/entities/shared';
import { Nullable } from '@paradigm/utils/types';
import { useDrfqOrdersFetcher, useSplittedOrders } from './hooks';

interface OpenAuction {
  readonly state: 'OPEN_AUCTION';
  readonly bid: Order | null;
  readonly ask: Order | null;
}

interface ClosedAuction {
  readonly state: 'CLOSED_AUCTION';
  readonly bid: Order | null;
  readonly ask: Order | null;
}

interface LoadingOrders {
  readonly state: 'LOADING_ORDERS';
  readonly bid: null;
  readonly ask: null;
}

type MakerOrdersView = LoadingOrders | OpenAuction | ClosedAuction;

const [useDrfqMakerOrders, DrfqMakersCtxProvider] =
  createSafeContext<MakerOrdersView>('MakerOrdersView');

export { useDrfqMakerOrders };

export default function DrfqMakersProvider({
  drfq,
  children,
}: PropsWithChildren<{ drfq: Nullable<Drfq> }>) {
  const emptyView: MakerOrdersView = {
    state: 'LOADING_ORDERS',
    bid: null,
    ask: null,
  };

  if (drfq == null || !isDrfq(drfq))
    return (
      <DrfqMakersCtxProvider value={emptyView}>
        {children}
      </DrfqMakersCtxProvider>
    );

  return (
    <FilledDrfqMakersProvider drfq={drfq}>{children}</FilledDrfqMakersProvider>
  );
}

function FilledDrfqMakersProvider({
  drfq,
  children,
}: PropsWithChildren<{ drfq: Drfq }>) {
  const { isLoading } = useDrfqOrdersFetcher(drfq.id);
  const { bids, asks } = useSplittedOrders(drfq.id);

  const view = createView({
    drfq,
    bids,
    asks,
    isLoading,
  });
  return <DrfqMakersCtxProvider value={view}>{children}</DrfqMakersCtxProvider>;
}

interface CreateView {
  readonly drfq?: Drfq;
  readonly bids: readonly Order[];
  readonly asks: readonly Order[];
  readonly isLoading: boolean;
}

function createView({
  drfq,
  bids,
  asks,
  isLoading,
}: CreateView): MakerOrdersView {
  if (isLoading) {
    return { state: 'LOADING_ORDERS', ask: null, bid: null };
  }

  const ask = asks.find(filterDisplayedOrder) ?? null;
  const bid = bids.find(filterDisplayedOrder) ?? null;
  const isClosed =
    drfq != null && ['CLOSED', 'RECENTLY_CLOSED'].includes(drfq.state);
  const state: MakerOrdersView['state'] = isClosed
    ? 'CLOSED_AUCTION'
    : 'OPEN_AUCTION';

  return {
    state,
    bid,
    ask,
  };
}

function filterDisplayedOrder(order: Order) {
  return isOpenOrder(order) || isOrderExecuted(order);
}
